// src/features/infoscreens/hooks/useScheduledReload.js
import { useState, useEffect } from 'react';

const RELOAD_INTERVAL_MS = 60 * 60 * 1000;
// Random spread so screens started together don't all reload at the same moment
const RELOAD_JITTER_MS = 5 * 60 * 1000;
const RETRY_DELAY_MS = 60 * 1000;
const OVERLAY_DURATION_MS = 2000;

/**
 * Periodically hard reloads the page so long-running screens pick up new
 * builds and shed accumulated state. Before reloading it checks the server is
 * reachable (a failed reload would leave the kiosk stuck on a browser error
 * page), then exposes `isReloading` for OVERLAY_DURATION_MS so the caller can
 * show an overlay.
 */
export function useScheduledReload() {
  const [isReloading, setIsReloading] = useState(false);

  useEffect(() => {
    let timeoutId;
    let cancelled = false;

    const schedule = (delay) => {
      timeoutId = setTimeout(attemptReload, delay);
    };

    const attemptReload = async () => {
      try {
        const response = await fetch(window.location.href, { cache: 'no-store' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
      } catch (err) {
        console.warn('Scheduled reload skipped, server unreachable:', err);
        if (!cancelled) schedule(RETRY_DELAY_MS);
        return;
      }

      if (cancelled) return;
      setIsReloading(true);
      timeoutId = setTimeout(() => window.location.reload(), OVERLAY_DURATION_MS);
    };

    schedule(RELOAD_INTERVAL_MS + Math.random() * RELOAD_JITTER_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  return isReloading;
}
